import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RECURRING_FREQUENCIES, RECURRING_FREQUENCY_LABELS, type RecurringFrequency } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/features/common/ui/Button';
import { Input } from '@/features/common/ui/Input';
import { Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from '@/features/common/ui/Sheet';

/**
 * "Repeat this" - a subscription made from an invoice that already exists.
 *
 * There is no line editor here on purpose. The customer, the lines, the taxes
 * and the currency come from the invoice behind it, which the pricing engine
 * has already priced; a schedule cannot be set up with a shape nothing has
 * ever validated. What this asks for is the four things the invoice cannot
 * know: how often, from when, until when, and how early to raise the draft.
 */
export function RepeatInvoiceSheet({
  invoice,
  onClose,
  onCreated,
}: {
  invoice: { id: string; number: string; date: string; customerName: string | null };
  onClose: () => void;
  onCreated: (profileId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(
    invoice.customerName ? `${invoice.customerName} - repeating` : `Repeat of ${invoice.number}`,
  );
  const [frequency, setFrequency] = useState<RecurringFrequency>('monthly');
  const [startDate, setStartDate] = useState(nextAfter(invoice.date, 'monthly'));
  const [endDate, setEndDate] = useState('');
  const [netDays, setNetDays] = useState('15');
  const [leadDays, setLeadDays] = useState('7');
  const [touchedStart, setTouchedStart] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/recurring', {
        profileName: name.trim(),
        fromInvoiceId: invoice.id,
        frequency,
        startDate,
        endDate: endDate || null,
        netDays: Number(netDays) || 0,
        leadDays: Number(leadDays) || 0,
      }),
    onSuccess: (profile) => {
      void queryClient.invalidateQueries({ queryKey: ['recurring'] });
      onCreated(profile.id);
    },
  });
  const error = create.error instanceof ApiError ? create.error : undefined;

  return (
    <Sheet labelledBy="repeat-title" onClose={onClose}>
      <SheetHeader titleId="repeat-title" title={`Repeat ${invoice.number}`} onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() && startDate) create.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            Fint will raise a <strong className="font-medium text-foreground">draft</strong> invoice each
            time this falls due, with the same lines and customer, priced at that date&rsquo;s rates. It
            never issues anything or emails anybody: the draft waits for you.
          </p>

          <Input
            label="Name"
            required
            maxLength={160}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hetzner CX42, re-billed to Acme"
            error={error?.fieldError('profileName')}
            data-testid="repeat-name"
          />

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium">How often</span>
            <select
              className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:min-h-9 sm:text-sm"
              value={frequency}
              onChange={(e) => {
                const next = e.target.value as RecurringFrequency;
                setFrequency(next);
                // The suggested first date follows the frequency until the
                // person sets one themselves - then it is theirs.
                if (!touchedStart) setStartDate(nextAfter(invoice.date, next));
              }}
              data-testid="repeat-frequency"
            >
              {RECURRING_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {RECURRING_FREQUENCY_LABELS[f]}
                </option>
              ))}
            </select>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="First one on"
              type="date"
              required
              value={startDate}
              onChange={(e) => {
                setTouchedStart(true);
                setStartDate(e.target.value);
              }}
              error={error?.fieldError('startDate')}
              data-testid="repeat-start"
            />
            <Input
              label="Until (optional)"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              error={error?.fieldError('endDate')}
              data-testid="repeat-end"
            />
            <Input
              label="Payment term (days)"
              type="number"
              min={0}
              max={365}
              value={netDays}
              onChange={(e) => setNetDays(e.target.value)}
              data-testid="repeat-net-days"
            />
            <Input
              label="Raise the draft (days early)"
              type="number"
              min={0}
              max={60}
              value={leadDays}
              onChange={(e) => setLeadDays(e.target.value)}
              hint="The invoice still carries its own date."
              data-testid="repeat-lead-days"
            />
          </div>

          {startDate ? (
            <p className="rounded-md bg-muted/40 p-3 text-sm" data-testid="repeat-summary">
              {RECURRING_FREQUENCY_LABELS[frequency]} from{' '}
              <strong className="font-medium">{readable(startDate)}</strong>
              {endDate ? ` until ${readable(endDate)}` : ''}. The first draft appears{' '}
              {Number(leadDays) > 0 ? `${leadDays} days before each date` : 'on the date itself'}.
            </p>
          ) : null}

          {error && !error.fieldError('profileName') && !error.fieldError('startDate') && !error.fieldError('endDate') ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} data-testid="repeat-create">
            Set it up
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/** One period after the source invoice - the date a repeat of it would fall on. */
function nextAfter(date: string, frequency: RecurringFrequency): string {
  const [y, m, d] = date.split('-').map(Number);
  if (frequency === 'weekly') return new Date(Date.UTC(y, m - 1, d + 7)).toISOString().slice(0, 10);
  const months = frequency === 'monthly' ? 1 : frequency === 'quarterly' ? 3 : frequency === 'half_yearly' ? 6 : 12;
  const absolute = y * 12 + (m - 1) + months;
  const year = Math.floor(absolute / 12);
  const month = (absolute % 12) + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`;
}

/** 04-10-2026, the way the rest of the app prints a date. */
function readable(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}
